import { Link } from "react-router-dom";
import { BiRightArrowAlt } from "react-icons/bi";
import financeScreen from "../../assets/homepage/FinanceScreen-web.png";
import StoreButtons from "../../components/StoreButtons";

// The cards around the phone copy real app screens, with numbers from the
// demo account (Birchwood Market Co.). Keep them matching the app: don't
// show anything the app doesn't do.
const DEMO_EVENT = {
  title: "Pumpkin Patch Makers Market",
  totalRevenue: 1540.44,
  totalCost: 337,
  additionalCosts: 50,
  netProfit: 1203.44,
};

// The same event's Financial Report → Products tab, top three.
const DEMO_BEST_SELLERS = [
  { name: "Birchwood Slice Necklace", sold: 9, revenue: "$270.00" },
  { name: "Copper Wire Wrapped Pendant", sold: 8, revenue: "$224.00" },
  { name: "Wildflower Pressed Resin Earrings", sold: 9, revenue: "$216.00" },
];

// The app prints amounts with toFixed(2), without thousands separators.
const appMoney = (value: number) => `$${value.toFixed(2)}`;

interface HeroProps {
  isAuthenticated: boolean;
  onStart: () => void;
}

const Hero = ({ isAuthenticated, onStart }: HeroProps) => {
  return (
    <section className="relative isolate overflow-hidden bg-[#070b17] px-5 pb-20 pt-28 text-white md:px-8 md:pb-28 md:pt-36">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(rgba(148,163,184,0.16)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]"
      />
      <div
        aria-hidden
        className="absolute -top-48 left-[10%] -z-10 h-[560px] w-[760px] rounded-full bg-blue-600/25 blur-[130px]"
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-[-10%] -z-10 h-[460px] w-[520px] rounded-full bg-violet-600/20 blur-[130px]"
      />

      <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.1fr_1fr] lg:gap-10">
        <div>
          <Link
            to="/pricing"
            className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 py-1 pl-1 pr-3 text-xs font-medium text-slate-300 transition hover:border-white/20"
          >
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
              New
            </span>
            Staff pay and commission, tracked per event
            <BiRightArrowAlt
              size={16}
              className="transition group-hover:translate-x-0.5"
            />
          </Link>

          <h1 className="mt-7 text-[2.6rem] font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
            Sell at the booth.
            <span className="block bg-gradient-to-r from-blue-400 via-indigo-300 to-violet-400 bg-clip-text text-transparent">
              Know what every show made.
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-400">
            VendorOrbis is the point-of-sale and back office for craft fairs,
            markets, festivals, and pop-ups. Take payments, track inventory per
            event, pay your staff, and see your profit before you pack up.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onStart}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-7 py-3.5 font-semibold text-white shadow-lg shadow-blue-600/30 transition hover:brightness-110"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start for free"}
              <BiRightArrowAlt size={20} />
            </button>
            <Link
              to="/pricing"
              className="rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white transition hover:bg-white/5"
            >
              See pricing
            </Link>
          </div>
          {!isAuthenticated && (
            <p className="mt-4 text-sm text-slate-500">
              Free forever for up to 10 active events and 25 items. No credit card.
            </p>
          )}

          <StoreButtons variant="glass" className="mt-10" />
        </div>

        <div className="relative mx-auto w-full max-w-[460px]">
          <div
            aria-hidden
            className="absolute inset-x-10 top-16 bottom-10 -z-10 rounded-full bg-blue-500/30 blur-[90px]"
          />
          <img
            src={financeScreen}
            alt="VendorOrbis Finance Overview showing net profit, revenue, costs, refunds, and employee payouts"
            className="mx-auto w-[78%] drop-shadow-[0_40px_60px_rgba(0,0,0,0.55)]"
          />

          {/* The toast the app shows after every sale, repeating like a busy booth */}
          <div className="vo-toast-loop absolute -left-2 top-[19%] w-[250px] sm:-left-12">
            <div className="flex h-[60px] overflow-hidden rounded-md bg-white shadow-2xl shadow-black/40">
              <span className="w-[5px] flex-shrink-0 bg-[#69C779]" />
              <div className="flex flex-col justify-center px-4">
                <p className="text-[13px] font-bold text-black">Payment Successful</p>
                <p className="text-xs text-neutral-500">Payment has been made</p>
              </div>
            </div>
          </div>

          {/* An Inventory item under its low-stock level, with the LOW badge */}
          <div className="vo-float absolute -right-2 top-[46%] flex w-[236px] overflow-hidden rounded-lg bg-[#202940] shadow-2xl shadow-black/40 sm:-right-8">
            <div className="relative flex h-[84px] w-[84px] flex-shrink-0 items-center justify-center bg-[#2c3654] pt-4 text-lg font-bold text-slate-300">
              HH
              <span className="absolute left-1.5 top-1.5 rounded-md bg-[#FF453A] px-1.5 py-px text-[10px] font-semibold text-white">
                LOW
              </span>
            </div>
            <div className="flex min-w-0 flex-col justify-center px-3 py-2">
              <p className="text-sm font-semibold leading-snug text-white">
                Hammered Hoop Earrings
              </p>
              <p className="mt-0.5 text-xs text-slate-400">Quantity: 6</p>
              <p className="mt-0.5 text-xs text-slate-400">Category: Jewelry</p>
            </div>
          </div>

          {/* An event's Finance tab */}
          <div className="vo-float-delayed absolute -left-2 bottom-[6%] w-[246px] rounded-xl bg-[#202940] p-4 shadow-2xl shadow-black/40 sm:-left-6 sm:bottom-auto sm:top-[50%]">
            <p className="truncate text-[11px] text-slate-400">{DEMO_EVENT.title}</p>
            <p className="mt-0.5 text-sm font-semibold text-white">Event Finance Details</p>
            <dl className="mt-2 space-y-1 text-xs">
              {[
                ["Total Revenue:", DEMO_EVENT.totalRevenue],
                ["Total Cost:", DEMO_EVENT.totalCost],
                ["Additional Costs:", DEMO_EVENT.additionalCosts],
              ].map(([label, value]) => (
                <div key={label as string} className="flex justify-between">
                  <dt className="text-slate-400">{label}</dt>
                  <dd className="tabular-nums text-white">{appMoney(value as number)}</dd>
                </div>
              ))}
              <div className="flex justify-between pt-1.5 text-sm">
                <dt className="text-white">Net Profit:</dt>
                <dd className="font-bold tabular-nums text-white">
                  {appMoney(DEMO_EVENT.netProfit)}
                </dd>
              </div>
            </dl>
          </div>

          {/* The event's best sellers; no room beside the phone on small screens */}
          <div className="vo-float absolute -right-8 bottom-[1%] hidden w-[292px] rounded-xl bg-[#202940] p-3 shadow-2xl shadow-black/40 sm:block">
            <p className="px-1 text-[11px] text-slate-400">Products · best sellers</p>
            {DEMO_BEST_SELLERS.map((item, index) => (
              <div
                key={item.name}
                className="mt-2 flex items-center gap-2.5 border-t border-white/10 px-1 pt-2"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#2c3245] text-[11px] text-slate-300">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-white">{item.name}</p>
                  <p className="text-[11px] text-slate-400">Quantity sold: {item.sold}</p>
                </div>
                <span className="text-xs tabular-nums text-white">{item.revenue}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
